import React, { useEffect, useState } from "react";
import { Textarea } from "@/components/ui/textarea";
import { CheckCircle2, AlertTriangle, XCircle, ChevronDown } from "lucide-react";

export const PRACTICE_OPTIONS = [
  { value: "met", label: "Met", icon: CheckCircle2, cls: "border-rag-green bg-rag-green-bg text-rag-green" },
  { value: "attention", label: "Needs attention", icon: AlertTriangle, cls: "border-rag-amber bg-rag-amber-bg text-rag-amber" },
  { value: "not_met", label: "Not met", icon: XCircle, cls: "border-rag-red bg-rag-red-bg text-rag-red" },
];

function StatusPicker({ areaKey, value, onChange }) {
  return (
    <div role="radiogroup" className="flex flex-wrap gap-2">
      {PRACTICE_OPTIONS.map((o) => {
        const active = value === o.value;
        return (
          <button key={o.value} type="button" role="radio" aria-checked={active} data-testid={`practice-${areaKey}-${o.value}`}
            onClick={() => onChange(active ? null : o.value)}
            className={`inline-flex items-center gap-1.5 rounded-full border px-3.5 py-1.5 text-sm font-semibold transition-colors ${active ? o.cls : "border-cream-deep bg-white text-foreground/70 hover:border-brand/40"}`}>
            <o.icon className="h-4 w-4" />{o.label}
          </button>
        );
      })}
    </div>
  );
}

const STATUS_PILL = { met: "bg-rag-green-bg text-rag-green", attention: "bg-rag-amber-bg text-rag-amber", not_met: "bg-rag-red-bg text-rag-red" };
const STATUS_WORD = { met: "Met", attention: "Needs attention", not_met: "Not met" };

function AreaCard({ area, saved, onChange, collapsible = false, open = true, onToggle }) {
  const needsAction = saved.status === "attention" || saved.status === "not_met";
  const missing = needsAction && !(saved.action || "").trim();
  const expanded = !collapsible || open || missing;
  return (
    <div id={`practice-${area.key}`} data-testid={`practice-area-${area.key}`} className="rounded-2xl bg-cream-card border border-cream-deep p-5 scroll-mt-24">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <button type="button" onClick={collapsible ? onToggle : undefined} data-testid={`practice-toggle-${area.key}`} className={`text-left flex items-start gap-2 min-w-0 ${collapsible ? "cursor-pointer" : "cursor-default"}`} aria-expanded={expanded}>
          {collapsible && <ChevronDown className={`h-4 w-4 mt-1 text-brand transition-transform ${expanded ? "rotate-180" : ""}`} />}
          <span>
            <h5 className="font-heading font-bold text-brand">{area.title}</h5>
            <p className="text-xs text-muted-foreground">{area.subtitle}</p>
          </span>
        </button>
        {expanded ? <StatusPicker areaKey={area.key} value={saved.status} onChange={(v) => onChange(area.key, "status", v)} />
          : <span data-testid={`practice-summary-${area.key}`} className={`text-xs font-semibold px-2.5 py-1 rounded-full ${saved.status ? STATUS_PILL[saved.status] : "bg-cream text-foreground/70"}`}>{saved.status ? STATUS_WORD[saved.status] : "Not yet confirmed"}</span>}
      </div>
      {!expanded ? null : <>
      {collapsible && <p className="text-xs text-muted-foreground mt-3">Choose the option that best describes current practice across the home, based on the checks below. An action is only needed where practice isn't fully met.</p>}
      <ul className="mt-3 grid sm:grid-cols-3 gap-x-4 gap-y-1">
        {area.prompts.map((pr) => <li key={pr} className="text-sm text-foreground/80 flex gap-2"><span className="text-coral mt-1.5 h-1.5 w-1.5 rounded-full bg-coral shrink-0" />{pr}</li>)}
      </ul>
      {needsAction && (
        <div className="mt-3">
          <Textarea data-testid={`practice-action-${area.key}`} value={saved.action || ""} onChange={(e) => onChange(area.key, "action", e.target.value)} rows={2}
            placeholder="What action is needed, who is responsible, and by when?" className={`bg-white text-sm ${missing ? "border-rag-red" : ""}`} />
          {missing && <p data-testid={`practice-action-required-${area.key}`} className="text-xs text-rag-red mt-1">An action is required before you can sign off.</p>}
        </div>
      )}
      </>}
    </div>
  );
}

export function PracticeAreas({ areas, practice, onChange, guided = false, openKey = null }) {
  const confirmed = areas.filter((a) => practice[a.key]?.status).length;
  const firstUnconfirmed = areas.find((a) => !practice[a.key]?.status)?.key || null;
  const [open, setOpen] = useState(openKey || firstUnconfirmed);
  useEffect(() => { if (openKey) setOpen(openKey); }, [openKey]);
  return (
    <section id="practice-areas" data-testid="practice-areas" className="mb-8 scroll-mt-24">
      <div className="flex flex-wrap items-end justify-between gap-3 mb-3">
        <div>
          <h4 className="font-heading font-bold text-brand text-sm">Step 2 — Confirm what's happening in practice</h4>
          <p className="text-xs text-muted-foreground">Four areas. Choose Met, Needs attention or Not met — an action is only needed where practice isn't fully met.</p>
        </div>
        {guided && (
          <div className="min-w-[180px]" data-testid="practice-progress">
            <div className="text-xs font-semibold text-brand text-right">{confirmed} of {areas.length} areas confirmed</div>
            <div className="h-1.5 rounded-full bg-cream-deep mt-1 overflow-hidden"><div className="h-full bg-coral transition-[width] duration-300" style={{ width: `${areas.length ? (confirmed / areas.length) * 100 : 0}%` }} /></div>
          </div>
        )}
      </div>
      <div className="space-y-3">
        {areas.map((a) => <AreaCard key={a.key} area={a} saved={practice[a.key] || {}} onChange={onChange} collapsible={guided} open={open === a.key} onToggle={() => setOpen((o) => (o === a.key ? null : a.key))} />)}
      </div>
    </section>
  );
}
